import { useMemo } from "react";

import { DateTime } from "luxon";

import { useCustomerContext } from "../../../../Context/CustomerContext";
import { useCreateAlertHandler } from "../../../../Pages/CloudAnalytics/alerts/hooks";
import { type AnalyticsAlertWithRef } from "../../../../Pages/CloudAnalytics/alerts/types";
import ActiveItemsCard from "../ActiveItemsCard";
import { useIsSupportUser } from "../hooks";
import { LoadingCard } from "../LoadingCard";
import { CreateItemCard } from "./CreateItemCard";

export const ActiveAlertsCard = ({ alertList, loading }: { alertList: AnalyticsAlertWithRef[]; loading: boolean }) => {
  const filteredAlerts = useMemo(() => alertList.filter((alert) => alert.data.isValid), [alertList]);
  const { customer } = useCustomerContext();
  const isSupportUser = useIsSupportUser();

  const handleNewAlert = useCreateAlertHandler({
    mixpanelEventName: "analytics.alerts.new",
    baseUrl: `/customers/${customer.id}/monitor/alerts`,
    prevPageUrl: `/customers/${customer.id}/home`,
  });

  const ninetyDaysAgo = DateTime.now().minus({ days: 90 }).toJSDate();
  const oneDayAgo = DateTime.now().minus({ days: 1 }).toJSDate();

  const triggeredAlerts90DaysAgo = useMemo(
    () =>
      filteredAlerts?.filter(
        (alert) => alert.data.timeLastAlerted && alert.data.timeLastAlerted.toDate() > ninetyDaysAgo
      ),
    [filteredAlerts, ninetyDaysAgo]
  );

  const triggeredAlerts1DayAgo = useMemo(
    () =>
      filteredAlerts?.filter((alert) => alert.data.timeLastAlerted && alert.data.timeLastAlerted.toDate() > oneDayAgo),
    [filteredAlerts, oneDayAgo]
  );

  if (loading) {
    return <LoadingCard title="Active alerts" />;
  }

  if (filteredAlerts?.length === 0) {
    return (
      <CreateItemCard
        title="Active alerts"
        buttonLabel="Create alert"
        createMsg="You haven't set up any alerts yet"
        onButtonClick={handleNewAlert}
        buttonDataCy="create-alert-btn"
        disabled={isSupportUser}
      />
    );
  }

  return (
    <ActiveItemsCard
      title="Active alerts"
      value={filteredAlerts?.length ?? 0}
      cloudCostsAtRisk={triggeredAlerts90DaysAgo?.length ?? 0}
      dataIcon="trending down"
      trend={triggeredAlerts1DayAgo?.length ?? 0}
      trendColor="error.main"
      trendDate="yesterday"
      riskLabel="Alerts triggered in the last 90 days"
      riskIsNumber={true}
      buttonLink="./monitor/alerts"
      disabled={isSupportUser}
      trackingDescription="Active alerts"
      trackingEvent="Active alerts"
    />
  );
};
